import React, { useState, useMemo } from "react";
import { Reveal } from "../hooks/useReveal";
import { Slider } from "./ui/slider";
import { Calculator, TrendingUp } from "lucide-react";

const naira = (n) => `₦${Math.round(n).toLocaleString("en-NG")}`;
const KEEP = 0.925;
const TYPICAL_KEEP = 0.75;

function Control({ id, label, value, display, min, max, step, onChange }) {
  const sliderValue = useMemo(() => [value], [value]);
  return (
    <div data-testid={`calc-${id}`}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-white/65">{label}</span>
        <span data-testid={`calc-${id}-value`} className="font-display font-bold text-white text-lg">{display}</span>
      </div>
      <Slider
        data-testid={`calc-${id}-slider`}
        className="mt-3"
        value={sliderValue}
        min={min}
        max={max}
        step={step}
        aria-label={label}
        onValueChange={([v]) => onChange(v)}
      />
    </div>
  );
}

function Stat({ id, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
      <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
      <div data-testid={`calc-${id}`} className={`mt-1 font-display font-extrabold text-2xl ${accent ? "text-[#2fd645]" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

export default function EarningsCalculator() {
  const [trips, setTrips] = useState(12);
  const [fare, setFare] = useState(3500);
  const [days, setDays] = useState(6);

  const daily = trips * fare * KEEP;
  const weekly = daily * days;
  const monthly = weekly * 4.33;
  const extra = trips * fare * days * 4.33 * (KEEP - TYPICAL_KEEP);

  return (
    <section id="earnings" data-testid="earnings-calculator" className="relative py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-3xl border border-[#2fd645]/25 bg-[#0b100d] p-6 sm:p-10 grid lg:grid-cols-[1.1fr_1fr] gap-10">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#2fd645]">
                <Calculator className="h-4 w-4" /> Earnings calculator
              </span>
              <h2 className="mt-3 font-display font-extrabold text-white text-3xl sm:text-4xl">How much could you take home?</h2>
              <p className="mt-3 text-white/60">Move the sliders to match your week. You keep 92.5% of every fare.</p>
              <div className="mt-8 space-y-7">
                <Control id="trips" label="Trips per day" value={trips} display={trips} min={1} max={30} step={1} onChange={setTrips} />
                <Control id="fare" label="Average fare" value={fare} display={naira(fare)} min={1000} max={15000} step={250} onChange={setFare} />
                <Control id="days" label="Days per week" value={days} display={days} min={1} max={7} step={1} onChange={setDays} />
              </div>
            </div>
            <div className="flex flex-col gap-4 justify-center">
              <div className="grid grid-cols-2 gap-4">
                <Stat id="daily" label="Per day" value={naira(daily)} />
                <Stat id="weekly" label="Per week" value={naira(weekly)} />
              </div>
              <div className="rounded-2xl border border-[#2fd645]/40 bg-[#2fd645]/10 p-6">
                <div className="text-xs uppercase tracking-wider text-white/60">Estimated monthly take-home</div>
                <div data-testid="calc-monthly" className="mt-1 font-display font-extrabold text-4xl sm:text-5xl text-[#2fd645]">{naira(monthly)}</div>
                <div className="mt-4 flex items-start gap-2 text-sm text-white/75">
                  <TrendingUp className="h-4 w-4 mt-0.5 text-[#2fd645] shrink-0" />
                  <span data-testid="calc-extra">That&apos;s about <strong className="text-white">{naira(extra)}</strong> more each month than on apps that take 25%.</span>
                </div>
              </div>
              <p className="text-xs text-white/40">Estimates before tax, fuel and other costs. Actual earnings depend on demand and trips completed.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
